/**
 * SystemDetail.tsx — V5 system detail page.
 *
 * Tabs: Overview · Scope (V112-3-10) · Controls (worklist) · Activity
 * Route: /w/:slug/app/systems/:systemId
 */

import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { onboardingHref } from '../lib/onboarding-model';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  PageHeader,
  Select,
  SeverityBadge,
  Spinner,
} from '@aigovops/design-system';
import { useWorkspace } from '../lib/workspace';
import { SystemPrograms } from '../components/systems/SystemPrograms';
import { useWorkspaceQuery, useWorkspaceMutation } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import {
  getWorkspaceSystem,
  listSystemWorklist,
  linkControlPolicy,
  markControlNotApplicable,
  applyControlSpec,
  proposeControlSpec,
  bulkApplyBaseline,
  extractControlSpecFromFile,
  type ControlSpecExtractionResult,
  listSystemMonitoringBindings,
  listCiCheckSubmissions,
  listWorkspaceSystems,
  listSystemDependencies,
  addSystemDependency,
  removeSystemDependency,
  type WsSystemRow,
  type WorklistItem,
  type CiCheckVerdictRow,
  listControlSources,
  type ControlSource,
} from '../lib/api';
import { federalOffAction } from '../lib/api';
import { SourceTagLine, SourcesSection, sourcesForSystem } from '../components/controls/ControlSources';
import { fmtDate } from '../lib/format';
import { SystemStageStrip } from '../components/SystemStageStrip';
import { SystemBuildChecks } from '../components/SystemBuildChecks';
import { DataLineageEditor } from '../components/DataLineageEditor';
import { RequirementsPanel, DisclosurePanel } from '../components/SystemLifecyclePanels';
import { SystemMonitoringTab } from '../components/monitoring/SystemMonitoringTab';
import { ProviderSuggestions } from '../components/ProviderSuggestions';
import { SystemAiResources } from '../components/SystemAiResources';
import { SystemAuthorityPanel } from '../components/SystemAuthorityPanel';
import './stages/lifecycle.css';
import { SystemScope } from '../components/scope/SystemScope';
import { guarded } from '../components/scope/scope-guard';
import { SystemSweepSummary } from '../components/sweep/ProgramRollup';

type Tab = 'overview' | 'dependencies' | 'scope' | 'controls' | 'activity' | 'requirements' | 'disclosure' | 'monitoring' | 'authority' | 'lineage';

const TABS: { value: Tab; label: string }[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'requirements', label: 'Requirements' },
  { value: 'disclosure', label: 'Disclosures' },
  { value: 'monitoring', label: 'Monitoring' },
  { value: 'authority', label: 'Authority' },
  { value: 'dependencies', label: 'Dependencies' },
  { value: 'scope', label: 'Scope' },
  { value: 'controls', label: 'Controls' },
  { value: 'lineage', label: 'Data Lineage' },
  { value: 'activity', label: 'Activity' },
];

function formatFreshness(seconds: number | null): string {
  if (seconds === null) return 'Standing';
  if (seconds <= 2592000) return 'Monthly';
  if (seconds <= 7776000) return 'Quarterly';
  if (seconds <= 15552000) return 'Semi-annual';
  return 'Annual';
}

export const STATUS_TOKENS: Record<string, { label: string; color: string }> = {
  unaddressed: { label: 'Unaddressed', color: 'var(--text-dim)' },
  policy_linked: { label: 'Policy linked', color: 'var(--accent)' },
  monitoring_configured: { label: 'Monitoring', color: 'var(--accent)' },
  // V109-5: the text-grade status tokens (≥ 4.5:1 on the surface in light and dark); the *-mark ones are for dots and bars.
  active: { label: 'Active', color: 'var(--status-on-track)' },
  attention: { label: 'Attention', color: 'var(--status-at-risk)' },
  not_applicable: { label: 'N/A', color: 'var(--text-dim)' },
};

export function SystemDetail() {
  const { workspace, role } = useWorkspace();
  const { systemId } = useParams<{ systemId: string }>();
  const slug = workspace.slug;
  const [search, setSearch] = useSearchParams();
  // V112-3-10: the Classification tab became Scope; old ?tab=classification links still land on it.
  const tabParam = search.get('tab') === 'classification' ? 'scope' : search.get('tab');
  const activeTab: Tab = TABS.some(t => t.value === tabParam) ? tabParam as Tab : 'controls';
  useEffect(() => {
    if (search.get('tab') !== 'classification') return;
    const next = new URLSearchParams(search); next.set('tab', 'scope'); setSearch(next, { replace: true });
  }, [search, setSearch]);
  // V112-3-10: leaving the Scope tab with unsaved scope edits asks first (scope-guard; no-op otherwise).
  const setActiveTab = (tab: Tab, then?: () => void) => guarded(() => {
    const next = new URLSearchParams(search); next.set('tab', tab); setSearch(next); then?.();
  });

  const sys = useWorkspaceQuery(
    (s) => queryKeys.systemDetail(s, systemId ?? ''),
    (s) => systemId ? getWorkspaceSystem(s, systemId) : Promise.resolve({ system: null as any }),
  );

  if (sys.loading) return <Spinner label="Loading system" />;
  if (sys.error || !sys.data?.system) {
    return <Alert variant="error">{sys.error ? String(sys.error) : 'System not found'}</Alert>;
  }

  const system = sys.data.system;

  // V113-7 U-7: on the Monitoring tab the tabs sit directly under the system name (no lifecycle / sweep / provider
  // chrome above them), so a row action from the Monitoring page lands on monitoring.
  const monitoringView = activeTab === 'monitoring';
  // V112-3-10 (built-panel High): on Scope the tab's own summary strip leads, so the system-wide panels stay off it.
  const panelsOff = monitoringView || activeTab === 'scope';

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      {monitoringView ? (
        <>
          <p className="mon-crumbs"><Link to={`/w/${slug}/app/systems`}>Systems</Link> / {system.name}</p>
          <PageHeader variant="app" title={system.name} subtitle={system.description ?? undefined} headingId="system-h1"
            right={<Link to={`/w/${slug}/app/monitoring`}>All monitoring</Link>} />
        </>
      ) : (
      <PageHeader
        title={system.name}
        subtitle={system.description ?? undefined}
        right={<Link to={`/w/${slug}/app/systems/${encodeURIComponent(system.system_id)}/components`}>Components</Link>}
      />
      )}
      {!panelsOff ? (<>

      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        <Badge tone={system.lane === 'buying' ? 'accent' : 'neutral'}>{system.lane}</Badge>
        <Badge>{system.origin?.replace(/_/g, ' ') ?? '—'}</Badge>
        {system.exposure_level ? <Badge>{system.exposure_level}</Badge> : null}
        {system.classified_at ? <Badge>classified</Badge> : <Badge>unclassified</Badge>}
      </div>

      {/* V6-6-4: Per-system lifecycle stage strip */}
      {system.lane === 'building' ? (
        <SystemStageStrip
          system={system}
          basePath={`/w/${slug}/app/systems/${system.system_id}`}
        />
      ) : null}

      {/* V109-5-5 / built-UI #9: "In <system>" — this system's resources and what changed on them, Next naming the
          resource and the controls it affects; program readiness only for a single-system workspace (else All systems). */}
      <SystemSweepSummary slug={slug} prefix={`/w/${slug}/app`} canSweep={role === 'owner' || role === 'admin'} systemId={system.system_id} systemName={system.name} />

      {/* V101-1-9: providers this system uses that have no connector yet (name → provider view); below the system's own status. */}
      <ProviderSuggestions key={system.system_id} slug={slug} editable={role === 'owner' || role === 'admin'} systemId={system.system_id} systemName={system.name} limit={2} />
      {/* V101-3-8: models / vector stores / agents connections found at this system's providers. */}
      <SystemAiResources key={`air-${system.system_id}`} slug={slug} systemId={system.system_id} canDismiss={role === 'owner' || role === 'admin' || role === 'editor'} />
      </>) : null}

      <div
        role="tablist"
        aria-label="System detail tabs"
        style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)', borderBottom: '1px solid var(--border)' }}
      >
        {TABS.map((t) => (
          <button
            key={t.value}
            role="tab"
            id={`system-tab-${t.value}`}
            aria-controls="system-tab-panel"
            aria-selected={activeTab === t.value}
            tabIndex={activeTab === t.value ? 0 : -1}
            onKeyDown={e => {
              const index = TABS.findIndex(item => item.value === t.value);
              const next = e.key === 'ArrowRight' ? (index + 1) % TABS.length : e.key === 'ArrowLeft' ? (index + TABS.length - 1) % TABS.length : e.key === 'Home' ? 0 : e.key === 'End' ? TABS.length - 1 : -1;
              if (next >= 0) { e.preventDefault(); const to = TABS[next].value; setActiveTab(to, () => document.getElementById(`system-tab-${to}`)?.focus()); }
            }}
            onClick={() => setActiveTab(t.value)}
            style={{
              padding: 'var(--space-2) var(--space-3)',
              border: 'none',
              borderBottom: activeTab === t.value ? '2px solid var(--accent)' : '2px solid transparent',
              background: 'transparent',
              color: activeTab === t.value ? 'var(--text)' : 'var(--text-muted)',
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--font-size-2xs)',
              fontWeight: activeTab === t.value ? 600 : 400,
              cursor: 'pointer',
              minHeight: 44,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div id="system-tab-panel" role="tabpanel" aria-labelledby={`system-tab-${activeTab}`}>
      {activeTab === 'requirements' && <RequirementsPanel key={system.system_id} slug={slug} systemId={system.system_id} canEdit={role === 'owner' || role === 'admin'} />}
      {activeTab === 'disclosure' && <DisclosurePanel key={system.system_id} slug={slug} systemId={system.system_id} systemName={system.name} canEdit={role === 'owner' || role === 'admin'} />}
      {activeTab === 'monitoring' && <SystemMonitoringTab key={system.system_id} slug={slug} systemId={system.system_id} />}
      {activeTab === 'authority' && <SystemAuthorityPanel key={system.system_id} slug={slug} systemId={system.system_id} role={role} />}
      {activeTab === 'overview' && (
        <div className="v75-stack">
          <OverviewTab system={system} />
          {/* V112-3-2 (BL-40 item 1): the programs this system is in, with "Add to <program>". */}
          <SystemPrograms slug={slug} systemId={system.system_id} systemName={system.name} isAdmin={role === 'owner' || role === 'admin'} />
        </div>
      )}
      {activeTab === 'dependencies' && (
        <DependenciesTab slug={slug} system={system} />
      )}
      {activeTab === 'scope' && (
        <SystemScope key={system.system_id} slug={slug} system={system} canEdit={role === 'owner' || role === 'admin'} onSaved={() => { void sys.refetch(); }} />
      )}
      {activeTab === 'controls' && <ControlsWorklistTab slug={slug} systemId={system.system_id} />}
      {activeTab === 'activity' && <div className="v75-stack"><SystemBuildChecks key={system.system_id} slug={slug} systemId={system.system_id} /><ActivityTab slug={slug} systemId={system.system_id} /></div>}
      {activeTab === 'lineage' && <DataLineageEditor key={system.system_id} systemId={system.system_id} workspaceSlug={slug} readOnly={role !== 'owner' && role !== 'admin'} />}
      </div>
    </div>
  );
}

// ---- Dependencies ----

const DEP_TYPE_LABELS: Record<string, string> = {
  infrastructure: 'Infrastructure',
  model_provider: 'Model provider',
  data_processor: 'Data processor',
  api_service: 'API service',
  other: 'Other',
};

const DEP_TYPE_OPTIONS = Object.entries(DEP_TYPE_LABELS).map(([value, label]) => ({ value, label }));

function DependenciesTab({ slug: _slug, system }: { slug: string; system: WsSystemRow }) {
  const deps = useWorkspaceQuery(
    (s) => queryKeys.systemDependencies(s, system.system_id),
    (s) => listSystemDependencies(s, system.system_id),
  );
  const allSystems = useWorkspaceQuery(
    (s) => queryKeys.systems(s),
    (s) => listWorkspaceSystems(s),
  );
  const [addOpen, setAddOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [depType, setDepType] = useState('infrastructure');
  const [depNotes, setDepNotes] = useState('');

  const addDep = useWorkspaceMutation(
    (s, input: { systemId: string; body: { supplier_system_id: string; dependency_type: string; notes?: string } }) =>
      addSystemDependency(s, input.systemId, input.body),
    'dependency:changed',
  );
  const removeDep = useWorkspaceMutation(
    (s, input: { systemId: string; depId: string }) =>
      removeSystemDependency(s, input.systemId, input.depId),
    'dependency:changed',
  );

  const busy = addDep.isPending || removeDep.isPending;

  const buyingSystems = useMemo(() => {
    const existingIds = new Set((deps.data?.dependencies ?? []).map((d) => d.supplier_system_id));
    return (allSystems.data?.systems ?? []).filter(
      (s) => s.system_id !== system.system_id && !existingIds.has(s.system_id)
    );
  }, [allSystems.data, deps.data, system.system_id]);

  const handleAdd = useCallback(async () => {
    if (!selectedSupplier) return;
    try {
      await addDep.mutateAsync({
        systemId: system.system_id,
        body: {
          supplier_system_id: selectedSupplier,
          dependency_type: depType,
          notes: depNotes.trim() || undefined,
        },
      });
      setAddOpen(false); setSelectedSupplier(''); setDepNotes('');
    } catch { /* mutation error handled by TanStack Query */ }
  }, [system.system_id, selectedSupplier, depType, depNotes, addDep]);

  const handleRemove = useCallback(async (depId: string) => {
    try {
      await removeDep.mutateAsync({ systemId: system.system_id, depId });
    } catch { /* mutation error handled by TanStack Query */ }
  }, [system.system_id, removeDep]);

  if (deps.loading) return <Spinner />;

  const depList = deps.data?.dependencies ?? [];

  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)' }}>
          Infrastructure and third-party services this system depends on.
          Their compliance posture feeds your supply-chain controls.
        </div>
        {!addOpen ? (
          <Button variant="primary" compact onClick={() => setAddOpen(true)}>Add dependency</Button>
        ) : null}
      </div>

      {addOpen ? (
        <div style={{
          border: '1px solid var(--border)', borderRadius: 'var(--radius-card)',
          background: 'var(--surface)', padding: 'var(--space-3)', display: 'grid', gap: 'var(--space-2)',
        }}>
          <Field id="dep-supplier" label="Supplier / service">
            <Select id="dep-supplier" value={selectedSupplier} onChange={(e) => setSelectedSupplier(e.target.value)}>
              <option value="">Select a system...</option>
              {buyingSystems.map((s) => (
                <option key={s.system_id} value={s.system_id}>
                  {s.name} ({s.lane} · {s.origin?.replace(/_/g, ' ')})
                </option>
              ))}
            </Select>
          </Field>
          <Field id="dep-type" label="Dependency type">
            <Select id="dep-type" value={depType} onChange={(e) => setDepType(e.target.value)}>
              {DEP_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
          </Field>
          <Field id="dep-notes" label="Notes (optional)">
            <input id="dep-notes" value={depNotes} onChange={(e) => setDepNotes(e.target.value)}
              placeholder="e.g. Hosts production database"
              style={{
                width: '100%', padding: 'var(--space-1) var(--space-2)',
                border: '1px solid var(--border)', borderRadius: 'var(--radius-input, var(--radius-card))',
                background: 'var(--surface)', color: 'var(--text)', fontSize: 'var(--font-size-2xs)',
              }} />
          </Field>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button variant="primary" compact disabled={busy || !selectedSupplier} onClick={handleAdd}>
              {busy ? 'Adding...' : 'Add dependency'}
            </Button>
            <Button variant="ghost" compact disabled={busy} onClick={() => setAddOpen(false)}>Cancel</Button>
          </div>
        </div>
      ) : null}

      {depList.length === 0 && !addOpen ? (
        <EmptyState
          title="No dependencies registered"
          description="Add the infrastructure providers and third-party services this system runs on. Their compliance posture flows into your supply-chain controls."
        />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
          {depList.map((dep) => (
            <div key={dep.dependency_id} style={{
              display: 'flex', gap: 'var(--space-3)', alignItems: 'center',
              padding: 'var(--space-2) var(--space-3)',
              border: '1px solid var(--border)', borderRadius: 'var(--radius-card)',
              background: 'var(--surface)',
            }}>
              <span style={{ fontWeight: 600, color: 'var(--text)', fontSize: 'var(--font-size-2xs)', flex: 1 }}>
                {dep.supplier_name}
              </span>
              <Badge>{DEP_TYPE_LABELS[dep.dependency_type] ?? dep.dependency_type}</Badge>
              <Badge tone="neutral">{dep.supplier_lane}</Badge>
              {dep.notes ? (
                <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {dep.notes}
                </span>
              ) : null}
              <Button variant="ghost" compact disabled={busy} onClick={() => handleRemove(dep.dependency_id)}
                style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Overview ----

function OverviewTab({ system }: { system: WsSystemRow }) {
  const classification = system.classification_json
    ? (() => { try { return JSON.parse(system.classification_json); } catch { return null; } })()
    : null;

  return (
    <Card>
      <div style={{ padding: 'var(--space-3) var(--space-4)', display: 'grid', gap: 'var(--space-2)' }}>
        <Row label="Lane" value={system.lane} />
        <Row label="Origin" value={system.origin?.replace(/_/g, ' ') ?? '—'} />
        <Row label="Deployment" value={system.deployment_status} />
        <Row label="Risk tier" value={system.risk_tier ?? 'Not assessed'} />
        <Row label="Exposure" value={system.exposure_level ?? 'Not set'} />
        <Row label="Classified" value={system.classified_at ? fmtDate(system.classified_at) : 'No'} />
        {classification?.types && (
          <Row label="Technical classes" value={(classification.types as string[]).join(', ')} />
        )}
      </div>
    </Card>
  );
}

// ---- Controls Worklist ----

function ControlsWorklistTab({ slug, systemId }: { slug: string; systemId: string }) {
  const q = useWorkspaceQuery(
    (s) => queryKeys.worklist(s, systemId),
    (s) => listSystemWorklist(s, systemId),
  );
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [familyFilter, setFamilyFilter] = useState<string>('all');
  const [actionTarget, setActionTarget] = useState<string | null>(null);
  const [reviewTarget, setReviewTarget] = useState<{ controlId: string; specId: string; mode: 'approve' | 'copy' } | null>(null);
  const [extracting, setExtracting] = useState(false);
  // ADR-0073 §7: AI extraction is off for this workspace's data → the review form opens for manual entry.
  const [extractOff, setExtractOff] = useState(false);
  const [extraction, setExtraction] = useState<ControlSpecExtractionResult | null>(null);

  const linkPolicyMutation = useWorkspaceMutation(
    (s, input: { controlId: string; policyId: string }) =>
      linkControlPolicy(s, systemId, input.controlId, input.policyId),
    'worklist:changed',
  );
  const markNAMutation = useWorkspaceMutation(
    (s, input: { controlId: string; rationale: string }) =>
      markControlNotApplicable(s, systemId, input.controlId, input.rationale),
    'worklist:changed',
  );
  const applySpecMutation = useWorkspaceMutation(
    (s, input: { controlId: string; body: { spec_id: string; rationale: string } }) =>
      applyControlSpec(s, systemId, input.controlId, input.body),
    'worklist:changed',
  );
  const proposeSpecMutation = useWorkspaceMutation(
    (s, input: { controlId: string; body: { spec_version?: string; accepted_signals_json?: Array<{ kind: string }>; freshness_max_age_seconds?: number | null; verdict_policy?: string; rationale: string; source_kind: 'baseline_copy' | 'customer_upload' } }) =>
      proposeControlSpec(s, systemId, input.controlId, input.body),
    'worklist:changed',
  );
  const bulkApplyMutation = useWorkspaceMutation(
    (s, input: { rationale: string }) =>
      bulkApplyBaseline(s, systemId, input.rationale),
    'worklist:changed',
  );

  const busy = linkPolicyMutation.isPending || markNAMutation.isPending || applySpecMutation.isPending || proposeSpecMutation.isPending || bulkApplyMutation.isPending;

  const worklist = useMemo(() => {
    let items = q.data?.worklist ?? [];
    if (statusFilter !== 'all') items = items.filter((w) => w.state?.status === statusFilter);
    if (familyFilter !== 'all') items = items.filter((w) => w.family === familyFilter);
    return items;
  }, [q.data, statusFilter, familyFilter]);

  const counts = q.data?.counts ?? {};
  const families = useMemo(() => {
    const set = new Set((q.data?.worklist ?? []).map((w) => w.family));
    return Array.from(set).sort();
  }, [q.data]);

  // v11.6 V116-1-5 (ADR-0115): every row's sources in one request per 100 controls.
  const allControlIds = useMemo(() => (q.data?.worklist ?? []).map((w) => w.control_id).sort(), [q.data]);
  const sourcesQ = useWorkspaceQuery(
    (s) => queryKeys.controlSources(s, allControlIds.join(',')),
    async (s) => {
      const merged: Record<string, ControlSource[]> = {};
      for (let i = 0; i < allControlIds.length; i += 100) {
        Object.assign(merged, (await listControlSources(s, allControlIds.slice(i, i + 100))).sources);
      }
      return merged;
    },
    { enabled: allControlIds.length > 0, staleTime: 60_000 },
  );

  const handleLinkPolicy = useCallback(async (controlId: string, policyId: string) => {
    try {
      await linkPolicyMutation.mutateAsync({ controlId, policyId });
    } finally { setActionTarget(null); }
  }, [linkPolicyMutation]);

  const handleMarkNA = useCallback(async (controlId: string, rationale: string) => {
    try {
      await markNAMutation.mutateAsync({ controlId, rationale });
    } finally { setActionTarget(null); }
  }, [markNAMutation]);

  const handleApplySpec = useCallback(async (controlId: string, specId: string, rationale: string) => {
    try {
      await applySpecMutation.mutateAsync({ controlId, body: { spec_id: specId, rationale } });
      setReviewTarget(null);
    } catch { /* handled by TanStack Query */ }
  }, [applySpecMutation]);

  const handleProposeSpec = useCallback(async (controlId: string, body: {
    spec_version?: string;
    accepted_signals_json?: Array<{ kind: string }>;
    freshness_max_age_seconds?: number | null;
    verdict_policy?: string;
    rationale: string;
    source_kind: 'baseline_copy' | 'customer_upload';
  }) => {
    try {
      await proposeSpecMutation.mutateAsync({ controlId, body });
      setReviewTarget(null);
    } catch { /* handled by TanStack Query */ }
  }, [proposeSpecMutation]);

  const handleUploadSpec = useCallback(async (controlId: string, file: File) => {
    setExtracting(true);
    setExtractOff(false);
    try {
      const result = await extractControlSpecFromFile(slug, controlId, file);
      setExtraction(result);
      setReviewTarget({ controlId, specId: '', mode: 'copy' });
    } catch (err) {
      setExtraction(null);
      if (federalOffAction(err)) {
        // Parity control_spec_extraction → off with action: enter the criteria by hand, right here.
        setExtractOff(true);
        setReviewTarget({ controlId, specId: '', mode: 'copy' });
      } else {
        console.error('BYOP extraction failed:', err);
      }
    } finally {
      setExtracting(false);
    }
  }, [slug]);

  const [bulkRationale, setBulkRationale] = useState('');
  const [showBulkApply, setShowBulkApply] = useState(false);
  const unaddressedWithBaseline = useMemo(() =>
    (q.data?.worklist ?? []).filter(w => (w.state?.status ?? 'unaddressed') === 'unaddressed' && w.baseline_spec),
    [q.data]
  );
  const handleBulkApply = useCallback(async () => {
    try {
      await bulkApplyMutation.mutateAsync({ rationale: bulkRationale });
      setShowBulkApply(false);
      setBulkRationale('');
    } catch { /* handled by TanStack Query */ }
  }, [bulkRationale, bulkApplyMutation]);

  if (q.loading) return <Spinner />;
  if (q.error) return <Alert variant="error">{String(q.error)}</Alert>;

  const total = q.data?.applicable ?? 0;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      {extractOff ? (
        <Alert variant="info"><p>AI extraction is off for this workspace's data. Enter the criteria in the form.</p></Alert>
      ) : null}
      {/* Summary bar */}
      <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)' }}>
          {total} applicable controls
        </span>
        {Object.entries(counts).map(([status, count]) => (
          <button
            key={status}
            onClick={() => setStatusFilter(statusFilter === status ? 'all' : status)}
            style={{
              padding: '2px 8px', borderRadius: 'var(--radius-chip)',
              border: statusFilter === status ? '1px solid var(--accent)' : '1px solid var(--border)',
              background: statusFilter === status ? 'var(--accent-soft, var(--surface-elevated))' : 'var(--surface)',
              color: STATUS_TOKENS[status]?.color ?? 'var(--text-muted)',
              fontSize: 'var(--font-size-3xs)', cursor: 'pointer', fontFamily: 'var(--font-ui)',
            }}
          >
            {STATUS_TOKENS[status]?.label ?? status} ({count})
          </button>
        ))}
        <Select value={familyFilter} onChange={(e) => setFamilyFilter(e.target.value)}
          style={{ fontSize: 'var(--font-size-3xs)', padding: '2px 6px' }}>
          <option value="all">All families</option>
          {families.map((f) => <option key={f} value={f}>{f}</option>)}
        </Select>
      </div>

      {unaddressedWithBaseline.length > 0 && (
        <div style={{
          display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap',
          padding: 'var(--space-2) var(--space-3)',
          background: 'var(--surface-elevated, var(--surface))',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          fontSize: 'var(--font-size-2xs)',
        }}>
          {showBulkApply ? (
            <>
              <span style={{ color: 'var(--text-muted)' }}>
                Apply FedSec standard to {unaddressedWithBaseline.length} unaddressed controls:
              </span>
              <input
                value={bulkRationale}
                onChange={e => setBulkRationale(e.target.value)}
                placeholder="Rationale (required)"
                onClick={e => e.stopPropagation()}
                style={{
                  flex: 1, minWidth: 200, padding: 'var(--space-1) var(--space-2)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-input, var(--radius-card))',
                  background: 'var(--surface)', color: 'var(--text)',
                  fontSize: 'var(--font-size-2xs)',
                }}
              />
              <Button variant="primary" compact disabled={busy || !bulkRationale.trim()} onClick={handleBulkApply}>
                Approve all
              </Button>
              <Button variant="ghost" compact disabled={busy} onClick={() => setShowBulkApply(false)}>
                Cancel
              </Button>
            </>
          ) : (
            <Button variant="ghost" compact onClick={() => setShowBulkApply(true)}>
              Apply FedSec standard to all unaddressed ({unaddressedWithBaseline.length})
            </Button>
          )}
        </div>
      )}

      {worklist.length === 0 ? (
        <EmptyState title="No matching controls" description="Adjust the filter or classify the system to see applicable controls." />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
          {worklist.map((item) => (
            <WorklistRow
              key={item.control_id}
              item={item}
              expanded={actionTarget === item.control_id}
              busy={busy}
              extracting={extracting}
              slug={slug}
              onToggle={() => setActionTarget(actionTarget === item.control_id ? null : item.control_id)}
              onLinkPolicy={handleLinkPolicy}
              onMarkNA={handleMarkNA}
              onReview={(controlId, specId) => setReviewTarget({ controlId, specId, mode: 'approve' })}
              onCopy={(controlId, specId) => setReviewTarget({ controlId, specId, mode: 'copy' })}
              onUpload={handleUploadSpec}
              sources={sourcesForSystem(sourcesQ.data?.[item.control_id], systemId)}
              sourcesLoading={sourcesQ.loading}
              sourcesError={sourcesQ.error}
              onRetrySources={() => { void sourcesQ.refetch(); }}
            />
          ))}
        </div>
      )}

      {reviewTarget && (() => {
        const targetItem = worklist.find(w => w.control_id === reviewTarget.controlId);
        return targetItem ? (
          <SpecReviewModal
            item={targetItem}
            mode={reviewTarget.mode}
            extraction={extraction}
            onApprove={handleApplySpec}
            onPropose={handleProposeSpec}
            onClose={() => { setReviewTarget(null); setExtraction(null); }}
            busy={busy}
          />
        ) : null;
      })()}
    </div>
  );
}

function SpecReviewModal({ item, mode, extraction, onApprove, onPropose, onClose, busy }: {
  item: WorklistItem;
  mode: 'approve' | 'copy';
  extraction?: ControlSpecExtractionResult | null;
  onApprove: (controlId: string, specId: string, rationale: string) => void;
  onPropose: (controlId: string, body: {
    spec_version?: string;
    accepted_signals_json?: Array<{ kind: string }>;
    freshness_max_age_seconds?: number | null;
    verdict_policy?: string;
    rationale: string;
    source_kind: 'baseline_copy' | 'customer_upload';
  }) => void;
  onClose: () => void;
  busy: boolean;
}) {
  const [rationale, setRationale] = useState(extraction?.rationale ?? '');
  const spec = item.baseline_spec;
  const isCopy = mode === 'copy';
  const isUpload = !!(extraction && isCopy);

  // Determine initial values — extraction pre-fills when available, else baseline spec
  const initSignals = extraction
    ? extraction.accepted_signals.join(', ')
    : spec ? spec.accepted_signals.join(', ') : '';

  const initFreshnessSeconds = extraction
    ? extraction.freshness_suggestion_seconds
    : spec?.freshness_max_age_seconds ?? null;

  const initVerdictPolicy = extraction?.verdict_policy ?? 'all_must_pass';

  // Editable fields for copy mode
  const [signals, setSignals] = useState(initSignals);
  const [freshness, setFreshness] = useState<string>(
    initFreshnessSeconds === null ? 'standing'
    : initFreshnessSeconds <= 2592000 ? 'monthly'
    : initFreshnessSeconds <= 7776000 ? 'quarterly'
    : initFreshnessSeconds <= 15552000 ? 'semi-annual'
    : 'annual'
  );
  const [verdictPolicy, setVerdictPolicy] = useState(initVerdictPolicy);

  const FRESHNESS_OPTIONS: { value: string; label: string; seconds: number | null }[] = [
    { value: 'standing', label: 'Standing (no expiry)', seconds: null },
    { value: 'monthly', label: 'Monthly (30 days)', seconds: 2592000 },
    { value: 'quarterly', label: 'Quarterly (90 days)', seconds: 7776000 },
    { value: 'semi-annual', label: 'Semi-annual (180 days)', seconds: 15552000 },
    { value: 'annual', label: 'Annual (365 days)', seconds: 31536000 },
  ];

  const handleSubmitProposal = () => {
    const parsedSignals = signals.split(',').map(s => s.trim()).filter(Boolean);
    const freshnessEntry = FRESHNESS_OPTIONS.find(f => f.value === freshness);
    onPropose(item.control_id, {
      spec_version: spec?.spec_version ?? '1.0.0',
      accepted_signals_json: parsedSignals.map(kind => ({ kind })),
      freshness_max_age_seconds: freshnessEntry?.seconds ?? null,
      verdict_policy: verdictPolicy,
      rationale: rationale.trim(),
      source_kind: isUpload ? 'customer_upload' : 'baseline_copy',
    });
  };

  const inputStyle = {
    width: '100%', padding: 'var(--space-2)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-input, var(--radius-card))',
    background: 'var(--surface)', color: 'var(--text)',
    fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-2xs)',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.5)',
    }} onClick={onClose}>
      <div style={{
        background: 'var(--surface-elevated, var(--surface))',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        padding: 'var(--space-4)',
        maxWidth: 560, width: '100%',
        maxHeight: '80vh', overflow: 'auto',
        display: 'grid', gap: 'var(--space-3)',
      }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: 0, color: 'var(--text)' }}>
          {isCopy ? 'Customize control specification' : 'Review control specification'}
        </h3>

        <div style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)' }}>
          <strong>{item.control_id}</strong> — {item.title}
        </div>

        {isCopy ? (
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-card)', padding: 'var(--space-3)',
            display: 'grid', gap: 'var(--space-2)', fontSize: 'var(--font-size-2xs)',
          }}>
            <div><strong>Source:</strong> {isUpload ? 'Customer upload (extracted)' : `FedSec standard v${spec?.spec_version ?? '?'} (customized copy)`}</div>
            {isUpload && extraction && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
                padding: 'var(--space-1) var(--space-2)',
                borderRadius: 'var(--radius-chip)',
                background: extraction.confidence >= 0.8 ? 'var(--surface)' : extraction.confidence >= 0.5 ? 'var(--warning-soft, var(--surface))' : 'var(--critical-soft, var(--surface))',
                border: `1px solid ${extraction.confidence >= 0.8 ? 'var(--border)' : extraction.confidence >= 0.5 ? 'var(--warning)' : 'var(--critical)'}`,
                fontSize: 'var(--font-size-3xs)',
                color: extraction.confidence >= 0.8 ? 'var(--text)' : extraction.confidence >= 0.5 ? 'var(--warning)' : 'var(--critical)',
              }}>
                Confidence: {Math.round(extraction.confidence * 100)}%
                {extraction.confidence < 0.8 && extraction.confidence >= 0.5 && ' — review carefully'}
                {extraction.confidence < 0.5 && ' — suggestion only, verify all fields'}
              </div>
            )}

            {isUpload && extraction && extraction.source_excerpts.length > 0 && (
              <details style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>
                <summary style={{ cursor: 'pointer', fontWeight: 600, color: 'var(--text-dim)' }}>
                  Source excerpts ({extraction.source_excerpts.length})
                </summary>
                <div style={{
                  marginTop: 'var(--space-1)', display: 'grid', gap: 'var(--space-1)',
                  padding: 'var(--space-2)', background: 'var(--surface)',
                  border: '1px solid var(--border)', borderRadius: 'var(--radius-card)',
                }}>
                  {extraction.source_excerpts.map((excerpt, i) => (
                    <blockquote key={i} style={{
                      margin: 0, paddingLeft: 'var(--space-2)',
                      borderLeft: '2px solid var(--accent)',
                      fontStyle: 'italic',
                    }}>
                      {excerpt}
                    </blockquote>
                  ))}
                </div>
              </details>
            )}

            <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
              <label style={{ fontWeight: 600, color: 'var(--text)' }}>Evidence types (comma-separated)</label>
              <input
                value={signals}
                onChange={e => setSignals(e.target.value)}
                placeholder="e.g., ci_check, attestation, document_upload"
                onClick={e => e.stopPropagation()}
                style={inputStyle}
              />
            </div>

            <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
              <label style={{ fontWeight: 600, color: 'var(--text)' }}>Freshness requirement</label>
              <select
                value={freshness}
                onChange={e => setFreshness(e.target.value)}
                onClick={e => e.stopPropagation()}
                style={inputStyle}
              >
                {FRESHNESS_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
              <label style={{ fontWeight: 600, color: 'var(--text)' }}>Verdict policy</label>
              <select
                value={verdictPolicy}
                onChange={e => setVerdictPolicy(e.target.value as 'all_must_pass' | 'any' | 'weighted')}
                onClick={e => e.stopPropagation()}
                style={inputStyle}
              >
                <option value="all_must_pass">All must pass</option>
                <option value="any_pass">Any pass</option>
                <option value="majority_pass">Majority pass</option>
              </select>
            </div>
          </div>
        ) : spec ? (
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-card)', padding: 'var(--space-3)',
            display: 'grid', gap: 'var(--space-2)', fontSize: 'var(--font-size-2xs)',
          }}>
            <div><strong>Source:</strong> FedSec standard v{spec.spec_version}</div>
            <div><strong>Evidence types:</strong> {spec.accepted_signals.length > 0 ? spec.accepted_signals.join(', ') : 'none'}</div>
            <div><strong>Freshness:</strong> {formatFreshness(spec.freshness_max_age_seconds)}</div>
            <div><strong>Verdict:</strong> All must pass</div>
          </div>
        ) : null}

        {item.framework_refs.length > 0 && (
          <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
            Applicable frameworks: {item.framework_refs.join(' · ')}
          </div>
        )}

        <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
          <label style={{ fontWeight: 600, fontSize: 'var(--font-size-2xs)', color: 'var(--text)' }}>
            {isCopy ? 'Rationale for customization (required)' : 'Approval rationale (required)'}
          </label>
          <textarea
            value={rationale}
            onChange={e => setRationale(e.target.value)}
            placeholder={isCopy ? 'e.g., Adjusted evidence types to match our CI pipeline' : 'e.g., Applying FedSec baseline per org policy'}
            rows={2}
            style={{
              ...inputStyle,
              resize: 'vertical' as const,
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          {isCopy ? (
            <Button variant="primary" disabled={busy || !rationale.trim()}
              onClick={handleSubmitProposal}>
              Submit for review
            </Button>
          ) : (
            <Button variant="primary" disabled={busy || !rationale.trim()}
              onClick={() => onApprove(item.control_id, spec!.spec_id, rationale)}>
              Approve &amp; activate
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function WorklistRow({ item, expanded, busy, extracting, slug, onToggle, onLinkPolicy, onMarkNA, onReview, onCopy, onUpload, sources, sourcesLoading, sourcesError, onRetrySources }: {
  item: WorklistItem;
  expanded: boolean;
  busy: boolean;
  extracting: boolean;
  slug: string;
  onToggle: () => void;
  onLinkPolicy: (controlId: string, policyId: string) => void;
  onMarkNA: (controlId: string, rationale: string) => void;
  onReview: (controlId: string, specId: string) => void;
  onCopy: (controlId: string, specId: string) => void;
  onUpload: (controlId: string, file: File) => void;
  sources?: ControlSource[];
  sourcesLoading?: boolean;
  sourcesError?: string | null;
  onRetrySources?: () => void;
}) {
  const [naRationale, setNaRationale] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  // v11.7 decision page: "Back to <control>" returns here.
  const loc = useLocation();
  const status = item.state?.status ?? 'unaddressed';
  const token = STATUS_TOKENS[status] ?? STATUS_TOKENS.unaddressed;
  const hasPolicy = !!(item.state?.common_policy_id || item.state?.system_policy_artifact_id);
  const hasMonitoring = !!item.state?.monitoring_binding_id;

  return (
    <div style={{
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-card)',
      background: 'var(--surface)',
      overflow: 'hidden',
    }}>
      <div
        onClick={onToggle}
        role="button"
        aria-expanded={expanded}
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
        style={{
          display: 'grid',
          gridTemplateColumns: '64px 1fr auto',
          gap: 'var(--space-2)',
          padding: 'var(--space-2) var(--space-3)',
          alignItems: 'center',
          cursor: 'pointer',
        }}
      >
        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }}>
          {item.control_id}
        </span>
        <div style={{ minWidth: 0 }}>
          <span style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
            {item.title}
          </span>
          <SourceTagLine sources={sources} fallback={item.framework_refs} />
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          {hasPolicy && <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--accent)' }} title="Policy linked">Policy</span>}
          {hasMonitoring && <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--accent)' }} title="Monitoring active">Monitor</span>}
          <span style={{
            padding: '1px 6px', borderRadius: 'var(--radius-chip)',
            border: `1px solid ${token.color}`, color: token.color,
            fontSize: 'var(--font-size-3xs)', fontFamily: 'var(--font-ui)',
          }}>
            {token.label}
          </span>
        </div>
      </div>

      {expanded ? (
        <div style={{
          borderTop: '1px solid var(--border)',
          padding: 'var(--space-3)',
          display: 'grid',
          gap: 'var(--space-3)',
          fontSize: 'var(--font-size-2xs)',
          background: 'var(--surface-elevated, var(--surface))',
        }}>
          {item.description ? (
            <p style={{ margin: 0, color: 'var(--text-muted)' }}>{item.description}</p>
          ) : null}

          <SourcesSection
            sources={sources}
            loading={!!sourcesLoading && !sources}
            error={sources ? null : sourcesError}
            onRetry={onRetrySources}
            slug={slug}
            back={{ from: `${loc.pathname}${loc.search}`, fromLabel: item.title }}
            headingId={`sources-${item.control_id.replace(/[^A-Za-z0-9_-]/g, '-')}`}
          />

          {/* Policy section */}
          <div>
            <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 'var(--space-1)' }}>Policy coverage</div>
            {item.pending_proposal && (
              <div style={{
                padding: 'var(--space-1) var(--space-2)',
                background: 'var(--accent-soft, var(--surface-elevated))',
                border: '1px solid var(--accent)',
                borderRadius: 'var(--radius-card)',
                color: 'var(--accent)',
                marginBottom: 'var(--space-2)',
                fontSize: 'var(--font-size-3xs)',
              }}>
                Specification under review — submitted {fmtDate(item.pending_proposal.submitted_at)}
              </div>
            )}
            {item.spec ? (
              <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
                <div style={{ color: 'var(--text)' }}>
                  Policy specification — {item.spec.source === 'baseline' ? 'FedSec standard' : 'Custom'} {item.spec.spec_version}
                </div>
                <div style={{ color: 'var(--text-muted)' }}>
                  Verdict: {item.spec.verdict_policy} · Freshness: {formatFreshness(item.spec.freshness_max_age_seconds)} · Evidence: {item.spec.accepted_signals.length > 0 ? item.spec.accepted_signals.join(', ') : 'none'}
                </div>
                <div style={{ color: 'var(--text-dim)' }}>
                  Approved by {item.spec.approved_by} on {fmtDate(item.spec.approved_at)}
                </div>
                {item.spec.has_workspace_override && (
                  <div style={{ color: 'var(--warning)', fontSize: 'var(--font-size-3xs)' }}>
                    Workspace override active
                  </div>
                )}
              </div>
            ) : item.baseline_spec ? (
              <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
                <div style={{ color: 'var(--text-muted)' }}>
                  FedSec standard available for this control:
                </div>
                <div style={{ color: 'var(--text)' }}>
                  {item.baseline_spec.spec_version} · Evidence: {item.baseline_spec.accepted_signals.length > 0 ? item.baseline_spec.accepted_signals.join(', ') : 'none'} · Freshness: {formatFreshness(item.baseline_spec.freshness_max_age_seconds)}
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                  <Button variant="primary" compact disabled={busy}
                    onClick={(e) => { e.stopPropagation(); onReview(item.control_id, item.baseline_spec!.spec_id); }}>
                    Approve &amp; apply
                  </Button>
                  <Button variant="ghost" compact disabled={busy}
                    onClick={(e) => { e.stopPropagation(); onCopy(item.control_id, item.baseline_spec!.spec_id); }}>
                    Start from this →
                  </Button>
                  <Button variant="ghost" compact disabled={busy || extracting}
                    onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}>
                    {extracting ? 'Extracting…' : 'Use your own ↑'}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.md,.txt,.text"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        onUpload(item.control_id, file);
                        e.target.value = '';
                      }
                    }}
                  />
                </div>
              </div>
            ) : item.available_policies.length > 0 ? (
              <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
                <div style={{ color: 'var(--text-muted)' }}>Common policies available:</div>
                {item.available_policies.map((p) => (
                  <div key={p.policy_id} style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                    <span style={{ color: 'var(--text)' }}>{p.title}</span>
                    <Badge>{p.scope_type}</Badge>
                    <Button variant="primary" compact disabled={busy}
                      onClick={(e) => { e.stopPropagation(); onLinkPolicy(item.control_id, p.policy_id); }}>
                      Accept
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: 'var(--text-dim)' }}>
                No policy specification available for this control.
              </div>
            )}
          </div>

          {/* Monitoring section */}
          <div>
            <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 'var(--space-1)' }}>Monitoring</div>
            {hasMonitoring ? (
              <div style={{ color: 'var(--accent)' }}>Monitoring binding active</div>
            ) : (
              <div style={{ color: 'var(--text-dim)' }}>
                No monitoring configured.{' '}
                <Link
                  to={`/w/${slug}/app/settings/integrations`}
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: 'var(--accent)', fontSize: 'var(--font-size-2xs)' }}
                >
                  Connect a source →
                </Link>
              </div>
            )}
          </div>

          {/* Actions */}
          {status !== 'not_applicable' ? (
            <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
              <div style={{ fontWeight: 600, color: 'var(--text)' }}>Mark not applicable</div>
              <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                <input
                  value={naRationale}
                  onChange={(e) => setNaRationale(e.target.value)}
                  placeholder="Rationale (required)"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    flex: 1, padding: 'var(--space-1) var(--space-2)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-input, var(--radius-card))',
                    background: 'var(--surface)', color: 'var(--text)',
                    fontSize: 'var(--font-size-2xs)',
                  }}
                />
                <Button variant="ghost" compact disabled={busy || !naRationale.trim()}
                  onClick={(e) => { e.stopPropagation(); onMarkNA(item.control_id, naRationale); }}>
                  Mark N/A
                </Button>
              </div>
            </div>
          ) : (
            <div style={{ color: 'var(--text-dim)' }}>
              Not applicable — {item.state?.na_rationale ?? 'no rationale recorded'}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

// ---- Activity (merged Monitoring + CI) ----

const VERDICT_SEVERITY_MAP: Record<string, 'critical' | 'high' | 'elevated' | 'moderate' | 'standard'> = {
  critical: 'critical',
  high: 'high',
  medium: 'elevated',
  low: 'moderate',
};

function ActivityTab({ slug, systemId }: { slug: string; systemId: string }) {
  const bindings = useWorkspaceQuery(
    (s) => queryKeys.monitoringBindings(s, systemId),
    (s) => listSystemMonitoringBindings(s, systemId),
  );
  const submissions = useWorkspaceQuery(
    (s) => queryKeys.ciSubmissions(s, systemId),
    (s) => listCiCheckSubmissions(s, systemId),
  );
  const [expandedSubmission, setExpandedSubmission] = useState<string | null>(null);

  if (bindings.loading || submissions.loading) return <Spinner />;
  if (bindings.error || submissions.error) return <Alert variant="error">System activity could not be loaded. <Button onClick={() => { bindings.refetch(); submissions.refetch(); }}>Retry</Button></Alert>;

  const bindingList = bindings.data?.bindings ?? [];
  const subList = submissions.data?.submissions ?? [];
  const hasData = bindingList.length > 0 || subList.length > 0;

  if (!hasData) {
    return (
      <div className="v75-stack"><EmptyState
        title="No activity yet"
        description="Activity appears here when CI check artifacts are submitted or monitoring bindings are created for this system's controls."
      /><Link className="v75-action primary" to={onboardingHref(slug, 'first_run', { system: systemId })}>Set up governance check</Link><Link to="?tab=controls">Configure monitoring controls</Link></div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      {bindingList.length > 0 ? (
        <div>
          <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--text)', marginBottom: 'var(--space-2)' }}>
            Monitoring bindings ({bindingList.length})
          </div>
          <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
            {bindingList.map((b) => (
              <div key={b.binding_id} style={{
                display: 'grid', gridTemplateColumns: '80px 80px 100px 80px 120px',
                gap: 'var(--space-3)', padding: 'var(--space-2) var(--space-3)',
                border: '1px solid var(--border)', borderRadius: 'var(--radius-card)',
                background: 'var(--surface)', fontSize: 'var(--font-size-2xs)', alignItems: 'center',
              }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--text)' }}>{b.control_id}</span>
                <Badge tone={b.binding_status === 'active' ? 'accent' : 'neutral'}>{b.binding_status}</Badge>
                <span style={{ color: 'var(--text-muted)' }}>{b.last_pass_at ? fmtDate(b.last_pass_at) : '—'}</span>
                <span style={{ color: 'var(--text)' }}>streak {b.consecutive_pass_count}</span>
                <span style={{ color: b.freshness_expires_at && new Date(b.freshness_expires_at) < new Date() ? 'var(--critical)' : 'var(--text-muted)' }}>
                  {b.freshness_expires_at ? fmtDate(b.freshness_expires_at) : '—'}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {subList.length > 0 ? (
        <div>
          <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--text)', marginBottom: 'var(--space-2)' }}>
            CI submissions ({subList.length})
          </div>
          <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
            {subList.map((s) => {
              const checks = s.checks ?? [];
              const passes = checks.filter(ch => ch.status === 'pass' || ch.status === 'not_applicable' || ch.status === 'skipped_exception');
              const denies = checks.filter(ch => ch.status === 'fail');
              const isExpanded = expandedSubmission === s.submission_id;
              const hasChecks = checks.length > 0;

              return (
                <div key={s.submission_id} style={{
                  border: '1px solid var(--border)', borderRadius: 'var(--radius-card)',
                  background: 'var(--surface)', overflow: 'hidden',
                }}>
                  {/* Summary row — clickable when checks exist */}
                  <div
                    role={hasChecks ? 'button' : undefined}
                    tabIndex={hasChecks ? 0 : undefined}
                    aria-expanded={hasChecks ? isExpanded : undefined}
                    onClick={() => hasChecks && setExpandedSubmission(isExpanded ? null : s.submission_id)}
                    onKeyDown={hasChecks ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedSubmission(isExpanded ? null : s.submission_id); } } : undefined}
                    style={{
                      display: 'grid', gridTemplateColumns: '120px 100px 80px 100px 1fr auto',
                      gap: 'var(--space-3)', padding: 'var(--space-2) var(--space-3)',
                      fontSize: 'var(--font-size-2xs)', alignItems: 'center',
                      cursor: hasChecks ? 'pointer' : 'default',
                    }}
                  >
                    <span style={{ color: 'var(--text-muted)' }}>{fmtDate(s.submitted_at)}</span>
                    <span style={{ color: 'var(--text)' }}>{s.tool}</span>
                    <Badge>{s.source_format}</Badge>
                    <Badge tone={s.parse_status === 'success' ? 'accent' : 'neutral'}>{s.parse_status}</Badge>
                    <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>
                      {s.commit_sha ? s.commit_sha.slice(0, 8) : '—'}
                    </span>
                    {hasChecks ? (
                      <span style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', fontSize: 'var(--font-size-3xs)' }}>
                        {passes.length > 0 && <span style={{ color: 'var(--success, var(--accent))' }}>{passes.length} pass</span>}
                        {denies.length > 0 && <span style={{ color: 'var(--critical, var(--text))' }}>{denies.length} deny</span>}
                        <span style={{ color: 'var(--text-dim)', fontSize: '0.625rem' }}>{isExpanded ? '▲' : '▼'}</span>
                      </span>
                    ) : null}
                  </div>

                  {/* Expanded detail — per-requirement verdicts */}
                  {isExpanded && hasChecks ? (
                    <div style={{
                      borderTop: '1px solid var(--border)',
                      padding: 'var(--space-3)',
                      background: 'var(--surface-elevated, var(--surface))',
                      display: 'flex', flexDirection: 'column', gap: 'var(--space-3)',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <Badge tone={denies.length === 0 ? 'accent' : 'neutral'}>
                          {denies.length === 0 ? 'All requirements met' : `${denies.length} requirement${denies.length === 1 ? '' : 's'} denied`}
                        </Badge>
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
                          {passes.length} of {checks.length} controls passed
                        </span>
                      </div>

                      {/* Deny cards */}
                      {denies.length > 0 ? (
                        <section>
                          <h4 style={{ margin: '0 0 var(--space-2)', color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }}>
                            Failing requirements
                          </h4>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                            {denies.map(ch => (
                              <SubmissionDenyCard key={ch.check_id} check={ch} />
                            ))}
                          </div>
                        </section>
                      ) : null}

                      {/* Pass list */}
                      {passes.length > 0 ? (
                        <section>
                          <h4 style={{ margin: '0 0 var(--space-1)', color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }}>
                            Met requirements
                          </h4>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            {passes.map(ch => (
                              <div
                                key={ch.check_id}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
                                  padding: '4px 8px', borderBottom: '1px solid var(--border)',
                                  fontSize: '0.875rem',
                                }}
                              >
                                <Badge tone="accent">Met</Badge>
                                <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--text)', fontSize: 'var(--font-size-3xs)' }}>
                                  {ch.control_id}
                                </span>
                                <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)' }}>
                                  {ch.title !== ch.control_id ? ch.title : ''}
                                </span>
                              </div>
                            ))}
                          </div>
                        </section>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Renders a single deny verdict card for a CI submission check, mirroring GateResults DenyCard. */
function SubmissionDenyCard({ check }: { check: CiCheckVerdictRow }) {
  return (
    <Card>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, color: 'var(--text)' }}>{check.control_id}</span>
          {check.title !== check.control_id ? (
            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)' }}>{check.title}</span>
          ) : null}
          {check.severity ? (
            <SeverityBadge severity={VERDICT_SEVERITY_MAP[check.severity] ?? 'standard'} />
          ) : null}
        </div>

        <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 8px', fontSize: '0.875rem' }}>
          {check.citation ? (
            <>
              <dt style={{ color: 'var(--text-muted)' }}>Citation</dt>
              <dd style={{ margin: 0, color: 'var(--text)' }}>{check.citation}</dd>
            </>
          ) : null}

          {check.message ? (
            <>
              <dt style={{ color: 'var(--text-muted)' }}>Finding</dt>
              <dd style={{ margin: 0, color: 'var(--text)' }}>{check.message}</dd>
            </>
          ) : null}

          {check.remediation ? (
            <>
              <dt style={{ color: 'var(--text-muted)' }}>Remediation</dt>
              <dd style={{ margin: 0, color: 'var(--text)' }}>{check.remediation}</dd>
            </>
          ) : null}
        </dl>
      </div>
    </Card>
  );
}

// ---- Helpers ----

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', gap: 'var(--space-3)', fontSize: 'var(--font-size-2xs)' }}>
      <span style={{ color: 'var(--text-dim)', minWidth: 140, flexShrink: 0 }}>{label}</span>
      <span style={{ color: 'var(--text)' }}>{value}</span>
    </div>
  );
}
